import React, { useState, useEffect } from 'react';

import API from './config';
const COLORS = { bleu: '#1E3A5F', brun: '#7A4E2D', orange: '#F97316', vert: '#16A34A' };

export default function FicheDevis({ apiFetch, devisId, affaires, clients, peutEcrire=true, onRetour, onImprimer, onModified, onFacturer, onAcompte, onSupprimer, onEncaisser, onEnvoyer, showErreur, refreshKey=0 }) {
  const [devis, setDevis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [lignes, setLignes] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showFichierSigne, setShowFichierSigne] = useState(false);
  const [fichierSigneUrl, setFichierSigneUrl] = useState(null);
  const [facturesLiees, setFacturesLiees] = useState([]);

  const charger = () => {
    setLoading(true);
    apiFetch(`${API}/devis/${devisId}/detail`)
      .then(r => r.json())
      .then(d => {
        setDevis(d);
        setForm({ date_echeance_validite: d.version?.date_echeance_validite||'', notes: d.version?.notes||'' });
        setLignes(d.version?.lignes?.map(l => ({
          ordre: l.ordre, designation: l.designation, unite: l.unite||'', _libre: false,
          quantite: parseFloat(l.quantite), prix_unitaire_ht: parseFloat(l.prix_unitaire_ht)
        })) || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const chargerFactures = () => {
    apiFetch(`${API}/factures/`)
      .then(r => r.json())
      .then(f => setFacturesLiees(f.filter(x => x.devis_id === devisId)))
      .catch(() => {});
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { charger(); }, [devisId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { chargerFactures(); }, [devisId, refreshKey]);

  const showMsg = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 3000); };

  const UNITES = ['forfait', 'ml', 'm²', 'm³', 'u', 'h', 'j', 'lot', 'ensemble'];
  const addLigne = () => setLignes([...lignes, { ordre:lignes.length+1, designation:'', unite:'', _libre:false, quantite:1, prix_unitaire_ht:0 }]);
  const removeLigne = (i) => setLignes(lignes.filter((_,idx) => idx!==i));
  const updateLigne = (i, field, val) => setLignes(lignes.map((l,idx) => idx===i ? {...l, [field]:val} : l));
  const totalHT = () => lignes.reduce((s,l) => s + (parseFloat(l.quantite)||0)*(parseFloat(l.prix_unitaire_ht)||0), 0);

  const sauvegarder = async () => {
    setSaving(true);
    const payload = {
      date_echeance_validite: form.date_echeance_validite || null,
      notes: form.notes || null,
      lignes: lignes.filter(l => l.designation).map((l,i) => ({
        ordre: i+1, designation: l.designation, unite: l.unite||null,
        quantite: parseFloat(l.quantite)||1, prix_unitaire_ht: parseFloat(l.prix_unitaire_ht)||0
      }))
    };
    const r = await apiFetch(`${API}/devis/${devisId}`, { method:'PUT', headers:{'Content-Type':'application/json'}, body: JSON.stringify(payload) });
    if (r.ok) { charger(); setEditMode(false); showMsg('✅ Devis mis à jour !'); if(onModified) onModified(); }
    else { const err = await r.json(); alert(err.detail); }
    setSaving(false);
  };

  const passerEnCours = async () => {
    if (!window.confirm('Passer ce devis en cours ? Il ne sera plus modifiable.')) return;
    const r = await apiFetch(`${API}/devis/${devisId}/en-cours`, { method:'PATCH' });
    if (r.ok) { charger(); showMsg('✅ Devis passé en cours !'); if(onModified) onModified(); }
    else {
      const err = await r.json().catch(() => null);
      showErreur(r.status, err?.detail || 'Impossible de passer le devis en cours');
    }
  };

  const accepter = async () => {
    if (!window.confirm('Confirmer l\'acceptation de ce devis ?')) return;
    const r = await apiFetch(`${API}/devis/${devisId}/accepter`, { method:'PATCH' });
    if (r.ok) { charger(); showMsg('✅ Devis accepté !'); if(onModified) onModified(); }
    else {
      const err = await r.json().catch(() => null);
      showErreur(r.status, err?.detail || 'Impossible d\'accepter le devis');
    }
  };

  const refuser = async () => {
    if (!window.confirm('Confirmer le refus de ce devis ?')) return;
    const r = await apiFetch(`${API}/devis/${devisId}/refuser`, { method:'PATCH' });
    if (r.ok) { charger(); showMsg('❌ Devis refusé'); if(onModified) onModified(); }
    else {
      const err = await r.json().catch(() => null);
      showErreur(r.status, err?.detail || 'Impossible de refuser le devis');
    }
  };

  const uploadSigne = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('fichier', file);
      const r = await apiFetch(`${API}/devis/${devisId}/upload-signe`, { method: 'POST', body: fd });
      if (r.ok) { charger(); showMsg('✅ Document signé enregistré !'); }
      else {
        const err = await r.json().catch(() => null);
        showErreur(r.status, err?.detail || 'Erreur lors de l\'upload du document signé');
      }
    } catch (err) {
      showErreur(null, err?.message || 'Erreur réseau');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const dupliquer = async () => {
    const r = await apiFetch(`${API}/devis/${devisId}/dupliquer`, { method:'POST' });
    if (r.ok) {
      const data = await r.json();
      showMsg(`✅ Devis dupliqué → ${data.reference}`);
      if(onModified) onModified();
    }
  };

  if (loading) return <div style={{padding:'32px', textAlign:'center', color:'#6B7280'}}>Chargement...</div>;

  const ouvrirFichierSigne = async () => {
    const r = await apiFetch(`${API}/devis/${devisId}/fichier-signe`);
    if (!r.ok) { alert('Impossible de charger le fichier'); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    setFichierSigneUrl(url);
    setShowFichierSigne(true);
  };

  const fermerFichierSigne = () => {
    setShowFichierSigne(false);
    if (fichierSigneUrl) { URL.revokeObjectURL(fichierSigneUrl); setFichierSigneUrl(null); }
  };

  const telechargerFichierSigne = async () => {
    const r = await apiFetch(`${API}/devis/${devisId}/fichier-signe`);
    if (!r.ok) return;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = devis.fichier_signe_nom; a.click();
    URL.revokeObjectURL(url);
  };

  // Popup consultation fichier signé
  const popupFichierSigne = showFichierSigne && fichierSigneUrl && (
    <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:4000, display:'flex', flexDirection:'column'}}>
      <div style={{background:'#1E3A5F', padding:'12px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0}}>
        <span style={{color:'white', fontWeight:'700', fontSize:'15px'}}>📎 {devis.fichier_signe_nom}</span>
        <div style={{display:'flex', gap:'10px'}}>
          <button onClick={telechargerFichierSigne}
            style={{background:'#16A34A', color:'white', border:'none', padding:'8px 16px', borderRadius:'8px', cursor:'pointer', fontSize:'14px', fontWeight:'700'}}>
            📥 Télécharger
          </button>
          <button onClick={fermerFichierSigne} style={{background:'rgba(255,255,255,0.15)', color:'white', border:'none', padding:'8px 16px', borderRadius:'8px', cursor:'pointer', fontSize:'14px'}}>✕ Fermer</button>
        </div>
      </div>
      <div style={{flex:1, overflow:'auto', background:'#E5E7EB', display:'flex', justifyContent:'center', alignItems:'flex-start', padding:'24px'}}>
        {/\.(jpg|jpeg|png)$/i.test(devis.fichier_signe_nom)
          ? <img src={fichierSigneUrl} alt="Devis signé" style={{maxWidth:'100%', boxShadow:'0 4px 24px rgba(0,0,0,0.2)'}} />
          : <iframe src={fichierSigneUrl} title="Devis signé" style={{width:'210mm', height:'90vh', border:'none', boxShadow:'0 4px 24px rgba(0,0,0,0.2)'}} />
        }
      </div>
    </div>
  );
  if (!devis) return <div style={{padding:'32px', textAlign:'center', color:'#DC2626'}}>Devis introuvable</div>;

  const affaire = affaires.find(a => a.id === devis.affaire_id);
  const client = clients.find(c => c.id === affaire?.client_id);
  const version = devis.version;
  const statut = devis.statut_global;

  const badge = (s) => ({
    display:'inline-block', padding:'3px 12px', borderRadius:'20px', fontSize:'13px', fontWeight:'600',
    background: s==='brouillon' ? '#FEF9C3' : s==='accepte' ? '#EDE9FE' : s==='refuse' ? '#FEE2E2' : s==='en_cours' ? '#DBEAFE' : s==='facture' ? '#DCFCE7' : '#F3F4F6',
    color: s==='brouillon' ? '#CA8A04' : s==='accepte' ? '#7C3AED' : s==='refuse' ? '#DC2626' : s==='en_cours' ? '#1D4ED8' : s==='facture' ? '#16A34A' : '#6B7280'
  });

  const s = {
    card: { background:'white', borderRadius:'10px', padding:'24px', boxShadow:'0 2px 8px rgba(0,0,0,0.08)', marginBottom:'16px' },
    th: { background: COLORS.bleu, color:'white', padding:'10px 14px', textAlign:'left', fontSize:'13px' },
    td: { padding:'10px 14px', borderBottom:'1px solid #F3F4F6', fontSize:'14px' },
    label: { fontSize:'12px', color:'#6B7280', textTransform:'uppercase', marginBottom:'4px' },
    value: { fontSize:'14px', color:'#1F2937', fontWeight:'500' },
    input: { width:'100%', padding:'7px 10px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'13px', boxSizing:'border-box' },
    btn: (c) => ({ background:c, color:'white', border:'none', padding:'8px 16px', borderRadius:'8px', cursor:'pointer', fontSize:'14px', fontWeight:'600' }),
    btnSm: (c) => ({ background:c||'#E5E7EB', color:c?'white':'#374151', border:'none', padding:'5px 10px', borderRadius:'6px', cursor:'pointer', fontSize:'12px' }),
  };

  return (
    <div>
      {popupFichierSigne}
      {message && <div style={{position:'fixed', top:'20px', right:'20px', background:COLORS.bleu, color:'white', padding:'12px 20px', borderRadius:'8px', zIndex:2000}}>{message}</div>}

      <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'24px', flexWrap:'wrap'}}>
        <button onClick={onRetour} style={{background:'#E5E7EB', border:'none', padding:'8px 16px', borderRadius:'8px', cursor:'pointer', fontSize:'14px'}}>← Retour</button>
        <h2 style={{margin:0, color:COLORS.bleu, fontSize:'20px'}}>{devis.reference}</h2>
        <span style={badge(statut)}>{statut}</span>

        <div style={{marginLeft:'auto', display:'flex', gap:'8px', flexWrap:'wrap'}}>
          {/* Bouton dupliquer — visible si peut écrire */}
          {peutEcrire && <button onClick={dupliquer} style={s.btn('#6B7280')}>📋 Dupliquer</button>}

          {/* Boutons selon statut */}
          {peutEcrire && statut === 'brouillon' && !editMode && (
            <>
              <button onClick={() => setEditMode(true)} style={s.btn('#6B7280')}>✏️ Modifier</button>
              <button onClick={passerEnCours} style={s.btn(COLORS.bleu)}>📤 Envoyer au client</button>
              {onSupprimer && (
                <button onClick={() => { if(window.confirm('Supprimer ce devis ? Cette action est irréversible.')) onSupprimer(devisId); }} style={s.btn('#DC2626')}>🗑️ Supprimer</button>
              )}
            </>
          )}
          {peutEcrire && statut === 'en_cours' && (
            <>
              <button onClick={accepter} style={s.btn(COLORS.vert)}>✅ Accepté</button>
              <button onClick={refuser} style={s.btn('#DC2626')}>❌ Refusé</button>
            </>
          )}
          {peutEcrire && statut === 'accepte' && (
            <>
              {onAcompte && <button onClick={() => onAcompte(devisId)} style={s.btn('#0369A1')}>📥 Acompte</button>}
              {onFacturer && <button onClick={() => onFacturer(devisId)} style={s.btn(COLORS.vert)}>📄 Facture totale</button>}
            </>
          )}
          {statut === 'facture' && (
            <span style={{ fontSize:'13px', color:'#16A34A', fontWeight:'600', padding:'8px 12px', background:'#DCFCE7', borderRadius:'8px' }}>✅ Déjà transformé en facture</span>
          )}
          {editMode && (
            <>
              <button onClick={() => { setEditMode(false); charger(); }} style={s.btn('#6B7280')}>✕ Annuler</button>
              <button onClick={sauvegarder} disabled={saving} style={s.btn(COLORS.vert)}>{saving ? 'Sauvegarde...' : '✅ Sauvegarder'}</button>
            </>
          )}
          {!editMode && onEnvoyer && statut !== 'brouillon' && peutEcrire && (
            <button onClick={() => onEnvoyer(devisId)} style={s.btn('#0EA5E9')}>📧 Envoyer</button>
          )}
          {!editMode && <button onClick={() => onImprimer(devisId)} style={s.btn(COLORS.orange)}>🖨️ PDF</button>}
        </div>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', marginBottom:'16px'}}>
        <div style={s.card}>
          <h4 style={{color:COLORS.bleu, margin:'0 0 16px 0', fontSize:'15px'}}>📋 Informations devis</h4>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px'}}>
            <div><div style={s.label}>Référence</div><div style={s.value}>{devis.reference}</div></div>
            <div><div style={s.label}>Statut</div><span style={badge(statut)}>{statut}</span></div>
            <div><div style={s.label}>Créé le</div><div style={s.value}>{new Date(devis.created_at).toLocaleDateString('fr-FR')}</div></div>
            <div>
              <div style={s.label}>CGV applicables</div>
              <div style={s.value}>{devis.cgv_version?.label || '—'}</div>
            </div>
            <div>
              <div style={s.label}>Valable jusqu'au</div>
              {editMode
                ? <input style={s.input} type="date" value={form.date_echeance_validite||''} onChange={e => setForm({...form, date_echeance_validite:e.target.value})} />
                : <div style={s.value}>{version?.date_echeance_validite ? new Date(version.date_echeance_validite).toLocaleDateString('fr-FR') : '—'}</div>
              }
            </div>
          </div>
          <div style={{marginTop:'12px'}}>
            <div style={s.label}>Conditions particulières / Notes</div>
            {editMode
              ? <textarea style={{...s.input, minHeight:'120px', resize:'vertical', fontFamily:'inherit', lineHeight:'1.4'}} value={form.notes||''} onChange={e => setForm({...form, notes:e.target.value})} placeholder="Conditions du devis affichées sur le PDF (modes de paiement, acomptes, mentions B2B, etc.). Optionnel." rows={6} />
              : <div style={{...s.value, color:'#6B7280', fontStyle:version?.notes?'normal':'italic', whiteSpace:'pre-wrap'}}>{version?.notes||'Aucune note'}</div>
            }
          </div>
        </div>

        <div style={s.card}>
          <h4 style={{color:COLORS.brun, margin:'0 0 16px 0', fontSize:'15px'}}>🔨 Affaire & Client</h4>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px'}}>
            <div><div style={s.label}>Affaire</div><div style={s.value}>{affaire?.reference||'—'}</div></div>
            <div><div style={s.label}>Titre</div><div style={s.value}>{affaire?.titre||'—'}</div></div>
            <div><div style={s.label}>Client</div><div style={s.value}>{client?.nom} {client?.prenom||''}</div></div>
            <div><div style={s.label}>Téléphone</div><div style={s.value}>{client?.telephone||'—'}</div></div>
            <div><div style={s.label}>Ville chantier</div><div style={s.value}>{affaire?.ville_chantier||'—'}</div></div>
          </div>
        </div>
      </div>

      {(statut === 'accepte' || statut === 'facture') && (
        <div style={{...s.card, borderLeft: `4px solid ${devis.fichier_signe_nom ? COLORS.vert : '#F59E0B'}`}}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'12px'}}>
            <div>
              <h4 style={{color: devis.fichier_signe_nom ? COLORS.vert : '#D97706', margin:'0 0 4px 0', fontSize:'15px'}}>
                {devis.fichier_signe_nom ? '✅ Devis signé archivé' : '⚠️ Document signé manquant'}
              </h4>
              {devis.fichier_signe_nom
                ? <div style={{fontSize:'13px', color:'#6B7280'}}>Fichier : <strong>{devis.fichier_signe_nom}</strong></div>
                : <div style={{fontSize:'13px', color:'#9CA3AF'}}>Importez le scan ou la photo du devis signé par le client (PDF, JPG, PNG).</div>
              }
            </div>
            <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
              {devis.fichier_signe_nom && (
                <>
                  <button onClick={ouvrirFichierSigne}
                    style={{...s.btn(COLORS.bleu), fontSize:'13px', padding:'7px 14px'}}>
                    👁 Consulter
                  </button>
                  <a href={`${API}/devis/${devisId}/fichier-signe`} target="_blank" rel="noreferrer"
                    style={{...s.btn('#6B7280'), textDecoration:'none', fontSize:'13px', padding:'7px 14px'}}>
                    📥 Télécharger
                  </a>
                </>
              )}
              {peutEcrire && (
                <label style={{...s.btn(devis.fichier_signe_nom ? '#6B7280' : '#D97706'), fontSize:'13px', padding:'7px 14px', cursor:'pointer', display:'inline-block', margin:0}}>
                  {uploading ? 'Envoi...' : devis.fichier_signe_nom ? '🔄 Remplacer' : '📎 Importer'}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{display:'none'}} onChange={uploadSigne} disabled={uploading} />
                </label>
              )}
            </div>
          </div>
        </div>
      )}

      {facturesLiees.length > 0 && (() => {
        const totalDevis = parseFloat(devis?.version?.montant_ht || 0);
        const acomptes = facturesLiees.filter(f => f.type === 'acompte');
        const totalAcomptesFactures = acomptes.reduce((s,f) => s + parseFloat(f.montant_ht), 0);
        const totalAcomptesEncaisses = acomptes.reduce((s,f) => s + (parseFloat(f.montant_ht) - parseFloat(f.solde_restant)), 0);
        const resteAFacturer = Math.max(totalDevis - totalAcomptesFactures, 0);
        return (
        <div style={s.card}>
          <h4 style={{color:'#16A34A', margin:'0 0 12px 0', fontSize:'15px'}}>📄 Factures liées</h4>
          {acomptes.length > 0 && (
            <div style={{background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:'8px', padding:'12px 16px', marginBottom:'14px', display:'flex', flexWrap:'wrap', gap:'18px', alignItems:'center', fontSize:'13px'}}>
              <div><span style={{color:'#6B7280'}}>Total devis :</span> <strong style={{color:'#0F172A'}}>{totalDevis.toFixed(2)} €</strong></div>
              <div style={{color:'#9CA3AF'}}>·</div>
              <div><span style={{color:'#6B7280'}}>Acompte(s) facturé(s) :</span> <strong style={{color:'#0369A1'}}>{totalAcomptesFactures.toFixed(2)} €</strong> <span style={{color:'#16A34A', fontSize:'12px'}}>(dont {totalAcomptesEncaisses.toFixed(2)} € encaissé{totalAcomptesEncaisses > 1 ? 's' : ''})</span></div>
              <div style={{color:'#9CA3AF'}}>·</div>
              <div><span style={{color:'#6B7280'}}>Reste à facturer :</span> <strong style={{color: resteAFacturer > 0 ? '#DC2626' : '#16A34A'}}>{resteAFacturer.toFixed(2)} €</strong></div>
            </div>
          )}
          <table style={{width:'100%', borderCollapse:'collapse'}}>
            <thead><tr>
              {['Numéro','Type','Montant HT','Solde restant','Statut',''].map(h => (
                <th key={h} style={{background:'#16A34A', color:'white', padding:'8px 12px', textAlign:'left', fontSize:'12px'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {facturesLiees.map((f, i) => (
                <tr key={f.id} style={{background: i%2===0?'white':'#F9FAFB'}}>
                  <td style={{padding:'8px 12px', fontSize:'13px', fontWeight:'600'}}>{f.numero}</td>
                  <td style={{padding:'8px 12px', fontSize:'13px'}}>
                    {f.type === 'acompte'
                      ? <span style={{padding:'2px 8px', borderRadius:'12px', fontSize:'11px', fontWeight:'700', background:'#E0F2FE', color:'#0369A1'}}>Acompte</span>
                      : <span style={{padding:'2px 8px', borderRadius:'12px', fontSize:'11px', background:'#F3F4F6', color:'#6B7280'}}>Solde</span>}
                  </td>
                  <td style={{padding:'8px 12px', fontSize:'13px'}}>{parseFloat(f.montant_ht).toFixed(2)} €</td>
                  <td style={{padding:'8px 12px', fontSize:'13px', fontWeight:'600', color: parseFloat(f.solde_restant)>0 ? '#DC2626' : '#16A34A'}}>{parseFloat(f.solde_restant).toFixed(2)} €</td>
                  <td style={{padding:'8px 12px', fontSize:'13px'}}>
                    <span style={{padding:'3px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:'600',
                      background: f.statut==='payee'?'#EDE9FE':f.statut==='emise'?'#DBEAFE':f.statut==='partiellement_payee'?'#FEF3C7':'#F3F4F6',
                      color: f.statut==='payee'?'#7C3AED':f.statut==='emise'?'#1D4ED8':f.statut==='partiellement_payee'?'#D97706':'#6B7280'}}>{f.statut}</span>
                  </td>
                  <td style={{padding:'8px 12px'}}>
                    {peutEcrire && (f.statut === 'emise' || f.statut === 'partiellement_payee') && onEncaisser && (
                      <button style={{background:'#16A34A', color:'white', border:'none', padding:'5px 10px', borderRadius:'6px', cursor:'pointer', fontSize:'12px'}}
                        onClick={() => onEncaisser(f)}>💰 Encaisser</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        );
      })()}

      <div style={s.card}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px'}}>
          <h4 style={{color:COLORS.bleu, margin:0, fontSize:'15px'}}>📝 Détail des prestations</h4>
          {editMode && <button style={s.btn(COLORS.orange)} onClick={addLigne}>+ Ligne</button>}
        </div>
        <table style={{width:'100%', borderCollapse:'collapse', borderRadius:'8px', overflow:'hidden'}}>
          <thead>
            <tr>{['Désignation','Unité','Quantité','Prix HT','Total HT', editMode?'':''].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {lignes.map((l, i) => (
              <tr key={i} style={{background: i%2===0?'white':'#F9FAFB'}}>
                <td style={s.td}>{editMode ? <input style={s.input} value={l.designation} onChange={e => updateLigne(i,'designation',e.target.value)} /> : <span style={{fontWeight:'500'}}>{l.designation}</span>}</td>
                <td style={s.td}>{editMode
                  ? (l._libre
                      ? <div style={{display:'flex', gap:'2px'}}>
                          <input style={{...s.input, flex:1, minWidth:0}} value={l.unite} onChange={e => updateLigne(i,'unite',e.target.value)} placeholder="Saisie libre" autoFocus />
                          <button title="Retour à la liste" style={{background:'#E5E7EB', border:'none', borderRadius:'6px', padding:'4px 6px', cursor:'pointer', fontSize:'11px', flexShrink:0}} onClick={() => setLignes(lignes.map((l2,idx) => idx===i ? {...l2, unite:'', _libre:false} : l2))}>↩</button>
                        </div>
                      : <select style={{...s.input, padding:'6px 8px'}} value={l.unite} onChange={e => {
                          if (e.target.value === '__libre__') setLignes(lignes.map((l2,idx) => idx===i ? {...l2, unite:'', _libre:true} : l2));
                          else updateLigne(i,'unite',e.target.value);
                        }}>
                          <option value=''>— Unité —</option>
                          {UNITES.map(u => <option key={u} value={u}>{u}</option>)}
                          <option disabled>──────────</option>
                          <option value='__libre__'>✏️ Saisie libre...</option>
                        </select>)
                  : l.unite||'—'}</td>
                <td style={s.td}>{editMode ? <input style={{...s.input, width:'70px'}} type="number" value={l.quantite} onChange={e => updateLigne(i,'quantite',e.target.value)} /> : parseFloat(l.quantite).toFixed(2)}</td>
                <td style={s.td}>{editMode ? <input style={{...s.input, width:'90px'}} type="number" value={l.prix_unitaire_ht} onChange={e => updateLigne(i,'prix_unitaire_ht',e.target.value)} /> : `${parseFloat(l.prix_unitaire_ht).toFixed(2)} €`}</td>
                <td style={{...s.td, fontWeight:'700', color:COLORS.vert}}>
                  {editMode ? `${((parseFloat(l.quantite)||0)*(parseFloat(l.prix_unitaire_ht)||0)).toFixed(2)} €` : `${(parseFloat(l.quantite||0)*parseFloat(l.prix_unitaire_ht||0)).toFixed(2)} €`}
                </td>
                {editMode && <td style={s.td}>{lignes.length>1 && <button style={{...s.btnSm(), color:'#DC2626'}} onClick={() => removeLigne(i)}>✕</button>}</td>}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{marginTop:'16px', display:'flex', justifyContent:'flex-end'}}>
          <div style={{background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:'8px', padding:'16px 24px', textAlign:'right'}}>
            <div style={{fontSize:'13px', color:'#6B7280', marginBottom:'4px'}}>Total HT</div>
            <div style={{fontSize:'28px', fontWeight:'700', color:COLORS.vert}}>
              {editMode ? totalHT().toFixed(2) : (version?.lignes?.reduce((s,l)=>s+parseFloat(l.total_ht||0),0)||0).toFixed(2)} €
            </div>
            <div style={{fontSize:'12px', color:'#9CA3AF', marginTop:'4px'}}>TVA non applicable, art. 293B du CGI</div>
          </div>
        </div>
      </div>
    </div>
  );
}
